import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { Camera } from '../../services/camerasApi';
import type { Policy } from '../../services/policiesApi';
import {
  COMPOSITE_CONDITION_TYPES,
  COMPOSITE_RUNTIME_NOT_AVAILABLE_COPY,
  FORKLIFT_COMPOSITE_COMMISSIONING_COPY,
  SAME_SITE_COMPOSITE_COMMISSIONING_COPY,
  canonicalCompositeRulePayload,
  canonicalPatternPayload,
  compositeConditionForPolicyType,
  compositeHasForkliftTerm,
  compositeLocationLabel,
  COMPOSITE_MISSING_SOURCE_TARGET_COPY,
  compositeScopeForSave,
  compositeSourcePolicies,
  defaultCompositeRule,
  deriveCompositeScopeBinding,
  deriveCompositeTarget,
  presentCompositeRulePreview,
  readCompositeRule,
  readCompositeTarget,
  sourcePolicyBuyerLabel,
  type CompositeConditionType,
  type CompositeRuleV1,
  type CompositeScopeBinding,
} from './policyConfigPresets';

const CONDITION_OPTIONS: Array<{ value: CompositeConditionType; label: string }> = [
  { value: 'RESTRICTED_AREA_ENTRY', label: 'Restricted-area entry' },
  { value: 'PPE_NON_COMPLIANCE', label: 'PPE non-compliance' },
  { value: 'PERSON_FORKLIFT_PROXIMITY', label: 'Forklift-pedestrian proximity' },
];

export function compositeProductionEligibility(rule: CompositeRuleV1): {
  allowed: boolean;
  reason: string | null;
} {
  const hasForklift = compositeHasForkliftTerm(rule);
  if (hasForklift) {
    return { allowed: false, reason: 'COMPOSITE_PRODUCTION_ALLOWED=NO' };
  }
  const known = (rule.terms || []).every((term) =>
    (COMPOSITE_CONDITION_TYPES as readonly string[]).includes(term.condition_type),
  );
  if (!known) {
    return { allowed: false, reason: 'COMPOSITE_PRODUCTION_ALLOWED=NO' };
  }
  return { allowed: true, reason: null };
}

export interface CompositeAlertEditorProps {
  rule: CompositeRuleV1;
  onChange: (rule: CompositeRuleV1) => void;
  pattern?: { enabled?: boolean; min_events?: number; window_seconds?: number; cooldown_seconds?: number } | null;
  onPatternChange?: (pattern: { enabled: boolean; min_events?: number; window_seconds?: number; cooldown_seconds?: number } | { enabled: false } | null) => void;
  eligibility?: { COMPOSITE_PRODUCTION_ALLOWED?: string; reason?: string | null };
  sourcePolicies?: Policy[];
  cameras?: Camera[];
}

const CompositeAlertEditor: React.FC<CompositeAlertEditorProps> = ({
  rule,
  onChange,
  pattern,
  onPatternChange,
  eligibility,
  sourcePolicies = [],
  cameras = [],
}) => {
  const canonical = canonicalCompositeRulePayload(rule);
  const locationLabel = compositeLocationLabel(canonical, sourcePolicies, cameras);
  const preview = presentCompositeRulePreview(canonical, { locationLabel });
  const forkliftVisible = compositeHasForkliftTerm(canonical);
  const localEligibility = compositeProductionEligibility(canonical);
  const productionAllowed =
    eligibility?.COMPOSITE_PRODUCTION_ALLOWED ||
    (localEligibility.allowed ? 'YES' : 'NO');

  const update = (patch: Partial<CompositeRuleV1>) => {
    onChange({ ...canonical, ...patch });
  };

  const applySourcePolicy = (index: number, policyId: string) => {
    const next = [...canonical.terms];
    const term = next[index];
    if (!term) return;
    const policy = sourcePolicies.find((item) => item.policy_id === policyId);
    const condition = policy ? compositeConditionForPolicyType(policy.policy_type) : null;
    next[index] = {
      ...term,
      ...(condition ? { condition_type: condition } : {}),
      ...(policyId ? { source_policy_id: policyId } : {}),
    };
    if (!policyId) {
      delete next[index].source_policy_id;
    }
    const nextRule = { ...canonical, terms: next };
    onChange({
      ...nextRule,
      scope_binding: deriveCompositeScopeBinding(nextRule, sourcePolicies),
    });
  };

  const unusedConditions = COMPOSITE_CONDITION_TYPES.filter(
    (condition) => !canonical.terms.some((term) => term.condition_type === condition),
  );

  const addTerm = () => {
    if (canonical.terms.length >= 8 || unusedConditions.length === 0) return;
    const condition = unusedConditions[0];
    const slug = condition === 'RESTRICTED_AREA_ENTRY' ? 'rae' : condition === 'PPE_NON_COMPLIANCE' ? 'ppe' : 'forklift';
    update({
      terms: [
        ...canonical.terms,
        { term_id: slug, condition_type: condition, min_events: 1 },
      ],
    });
  };

  return (
    <Box data-testid="composite-alert-editor">
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        Composite Alert
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Alert when multiple canonical safety conditions satisfy a configured relationship
        in the same scope and window. This is not a Pattern Alert.
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }} data-testid="composite-runtime-blocked">
        {COMPOSITE_RUNTIME_NOT_AVAILABLE_COPY}
      </Alert>

      <TextField
        fullWidth
        label="Name"
        value={canonical.name}
        onChange={(e) => update({ name: e.target.value })}
        sx={{ mb: 2 }}
        inputProps={{ maxLength: 200, 'data-testid': 'composite-rule-name' }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Relationship</InputLabel>
        <Select
          label="Relationship"
          value={canonical.operator}
          onChange={(e) => update({ operator: e.target.value === 'ANY' ? 'ANY' : 'ALL' })}
          inputProps={{ 'data-testid': 'composite-operator' }}
        >
          <MenuItem value="ALL">ALL (every condition)</MenuItem>
          <MenuItem value="ANY">ANY (at least one condition)</MenuItem>
        </Select>
      </FormControl>

      {canonical.terms.map((term, index) => (
        <Box key={`${term.term_id}-${index}`} display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          {sourcePolicies.length > 0 && (
            <FormControl sx={{ minWidth: 320, flex: 1.4 }}>
              <InputLabel>Source policy</InputLabel>
              <Select
                label="Source policy"
                value={term.source_policy_id || ''}
                onChange={(e) => applySourcePolicy(index, String(e.target.value || ''))}
                inputProps={{ 'data-testid': `composite-term-source-policy-${index}` }}
                renderValue={(value) => {
                  const selected = sourcePolicies.find((item) => item.policy_id === value);
                  return selected ? sourcePolicyBuyerLabel(selected, cameras) : 'Any matching condition';
                }}
              >
                <MenuItem value="">Any matching condition</MenuItem>
                {compositeSourcePolicies(sourcePolicies).filter((policy) => {
                  if (policy.policy_id === term.source_policy_id) return true;
                  const condition = compositeConditionForPolicyType(policy.policy_type);
                  if (!condition) return false;
                  return !canonical.terms.some(
                    (other, otherIndex) => otherIndex !== index && other.condition_type === condition,
                  );
                }).map((policy) => (
                  <MenuItem key={policy.policy_id} value={policy.policy_id}>
                    {sourcePolicyBuyerLabel(policy, cameras)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControl sx={{ minWidth: 260, flex: 1 }}>
            <InputLabel>Condition</InputLabel>
            <Select
              label="Condition"
              value={term.condition_type}
              onChange={(e) => {
                const next = [...canonical.terms];
                next[index] = {
                  ...term,
                  condition_type: e.target.value as CompositeConditionType,
                };
                delete next[index].source_policy_id;
                update({ terms: next });
              }}
              inputProps={{ 'data-testid': `composite-term-condition-${index}` }}
            >
              {CONDITION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Minimum events"
            type="number"
            inputProps={{ min: 1, max: 100, 'data-testid': `composite-term-count-${index}` }}
            value={term.min_events}
            onChange={(e) => {
              const next = [...canonical.terms];
              next[index] = { ...term, min_events: Number(e.target.value) };
              update({ terms: next });
            }}
            sx={{ width: 180 }}
          />
          {canonical.terms.length > 2 && (
            <Button
              onClick={() => update({ terms: canonical.terms.filter((_, i) => i !== index) })}
            >
              Remove
            </Button>
          )}
        </Box>
      ))}

      <Button
        onClick={addTerm}
        disabled={canonical.terms.length >= 8 || unusedConditions.length === 0}
        sx={{ mb: 2 }}
      >
        Add condition
      </Button>

      {forkliftVisible && (
        <Alert severity="warning" sx={{ mb: 2 }} data-testid="composite-forklift-commissioning">
          {FORKLIFT_COMPOSITE_COMMISSIONING_COPY}
        </Alert>
      )}

      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        <TextField
          label="Window (seconds)"
          type="number"
          inputProps={{ min: 1, max: 86400, 'data-testid': 'composite-window' }}
          value={canonical.window_seconds}
          onChange={(e) => update({ window_seconds: Number(e.target.value) })}
          sx={{ minWidth: 180, flex: 1 }}
        />
        <FormControl sx={{ minWidth: 180, flex: 1 }}>
          <InputLabel>Scope</InputLabel>
          <Select
            label="Scope"
            value={canonical.scope_binding}
            onChange={(e) => update({ scope_binding: e.target.value as CompositeScopeBinding })}
            inputProps={{ 'data-testid': 'composite-scope' }}
          >
            <MenuItem value="SAME_ZONE">Same zone</MenuItem>
            <MenuItem value="SAME_CAMERA">Same camera</MenuItem>
            <MenuItem value="SAME_SITE">Same site</MenuItem>
          </Select>
        </FormControl>
        <TextField
          label="Cooldown (seconds)"
          type="number"
          inputProps={{ min: 0, max: 604800, 'data-testid': 'composite-cooldown' }}
          value={canonical.cooldown_seconds}
          onChange={(e) => update({ cooldown_seconds: Number(e.target.value) })}
          sx={{ minWidth: 180, flex: 1 }}
        />
      </Box>

      {canonical.scope_binding === 'SAME_SITE' && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="composite-same-site-note">
          {SAME_SITE_COMPOSITE_COMMISSIONING_COPY}
        </Alert>
      )}

      <FormControl sx={{ minWidth: 180, mb: 2 }}>
        <InputLabel>Severity</InputLabel>
        <Select
          label="Severity"
          value={canonical.severity}
          onChange={(e) =>
            update({
              severity: e.target.value as CompositeRuleV1['severity'],
            })
          }
        >
          <MenuItem value="low">Low</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="high">High</MenuItem>
          <MenuItem value="critical">Critical</MenuItem>
        </Select>
      </FormControl>

      <FormControlLabel
        control={
          <Checkbox
            checked={canonical.actions.includes('email.notify')}
            onChange={(e) => update({ actions: e.target.checked ? ['email.notify'] : [] })}
          />
        }
        label="Email notify"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={canonical.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
          />
        }
        label="Rule enabled"
      />

      <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }} data-testid="composite-rule-preview">
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Rule preview
        </Typography>
        <Typography variant="body2">{preview}</Typography>
      </Paper>

      {onPatternChange ? (
        <Box sx={{ mt: 2 }} data-testid="composite-pattern-config">
          <Typography variant="subtitle2">Pattern Alert</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Alert when this Composite keeps recurring. Uses the same Pattern configuration as physical conditions.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={pattern?.enabled === true}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  onPatternChange(
                    enabled
                      ? canonicalPatternPayload({
                        enabled: true,
                        minEvents: pattern?.min_events,
                        windowMinutes: Number(pattern?.window_seconds ?? 3600) / 60,
                        cooldownHours: Number(pattern?.cooldown_seconds ?? 14400) / 3600,
                      }) || { enabled: true, min_events: 5, window_seconds: 3600, cooldown_seconds: 14400 }
                      : { enabled: false },
                  );
                }}
              />
            }
            label="Alert when this condition keeps recurring"
          />
          {pattern?.enabled === true ? (
            <Box display="flex" gap={2} mt={2} flexWrap="wrap">
              <TextField
                label="Occurrences"
                type="number"
                inputProps={{ min: 2 }}
                value={pattern.min_events ?? 5}
                onChange={(e) => onPatternChange({
                  enabled: true,
                  min_events: Number(e.target.value),
                  window_seconds: pattern.window_seconds ?? 3600,
                  cooldown_seconds: pattern.cooldown_seconds ?? 14400,
                })}
                helperText="Minimum 2 qualified occurrences."
                sx={{ minWidth: 180, flex: 1 }}
              />
              <TextField
                label="Within (minutes)"
                type="number"
                inputProps={{ min: 1 }}
                value={Number(pattern.window_seconds ?? 3600) / 60}
                onChange={(e) => onPatternChange({
                  enabled: true,
                  min_events: pattern.min_events ?? 5,
                  window_seconds: Number(e.target.value) * 60,
                  cooldown_seconds: pattern.cooldown_seconds ?? 14400,
                })}
                helperText="Rolling event-time window."
                sx={{ minWidth: 180, flex: 1 }}
              />
              <TextField
                label="Cooldown (hours)"
                type="number"
                inputProps={{ min: 0 }}
                value={Number(pattern.cooldown_seconds ?? 14400) / 3600}
                onChange={(e) => onPatternChange({
                  enabled: true,
                  min_events: pattern.min_events ?? 5,
                  window_seconds: pattern.window_seconds ?? 3600,
                  cooldown_seconds: Number(e.target.value) * 3600,
                })}
                helperText="Pattern-only suppression after firing."
                sx={{ minWidth: 180, flex: 1 }}
              />
            </Box>
          ) : null}
        </Box>
      ) : null}

      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" data-testid="composite-production-eligibility">
        Production eligibility: {productionAllowed === 'YES' ? 'COMPOSITE_PRODUCTION_ALLOWED=YES' : 'COMPOSITE_PRODUCTION_ALLOWED=NO'}
        {productionAllowed === 'NO' ? ' (source authority; not the Phase 1 runtime block)' : ''}
      </Typography>
    </Box>
  );
};

export default CompositeAlertEditor;

export function emptyCompositeForm(): {
  policy_type: 'composite_alert';
  name: string;
  scope: Record<string, never>;
  config: { composite_rule: CompositeRuleV1 };
  alert: { severity: 'medium'; enabled: false };
  pattern: null;
  enabled: true;
} {
  const rule = canonicalCompositeRulePayload({ ...defaultCompositeRule(), name: '' });
  return {
    policy_type: 'composite_alert',
    name: '',
    scope: {},
    config: { composite_rule: rule },
    alert: { severity: 'medium', enabled: false },
    pattern: null,
    enabled: true,
  };
}

export function compositeFormFromStored(policy: {
  name?: string;
  description?: string;
  scope?: Record<string, any>;
  config?: Record<string, any>;
  pattern?: { enabled?: boolean; min_events?: number; window_seconds?: number; cooldown_seconds?: number } | null;
  enabled?: boolean;
  target?: { edge_device_id?: string | null; camera_id?: string | null } | null;
  edge_device_id?: string | null;
  camera_id?: string | null;
}): {
  policy_type: 'composite_alert';
  name: string;
  description: string;
  scope: Record<string, any>;
  target?: { edge_device_id: string; camera_id: string };
  config: { composite_rule: CompositeRuleV1 };
  alert: { severity: CompositeRuleV1['severity']; enabled: false };
  pattern: { enabled?: boolean; min_events?: number; window_seconds?: number; cooldown_seconds?: number } | undefined;
  enabled: boolean;
} {
  const rule = readCompositeRule(policy.config);
  const name = String(policy.name || rule.name || '');
  const target = readCompositeTarget(policy);
  return {
    policy_type: 'composite_alert',
    name,
    description: policy.description || '',
    scope: policy.scope || {},
    ...(target ? { target } : {}),
    config: { composite_rule: canonicalCompositeRulePayload({ ...rule, name }) },
    alert: { severity: rule.severity, enabled: false },
    pattern: policy.pattern || undefined,
    enabled: policy.enabled !== false,
  };
}

export function compositeSavePayload(formData: {
  name?: string;
  config?: Record<string, any>;
  scope?: Record<string, any>;
  target?: { edge_device_id?: string | null; camera_id?: string | null } | null;
  pattern?: { enabled?: boolean; min_events?: number; window_seconds?: number; cooldown_seconds?: number } | null;
  sourcePolicies?: Policy[];
}):
  | { error: string }
  | {
      policy_type: 'composite_alert';
      name: string;
      scope: { camera_ids: string[] } | Record<string, never>;
      target?: { edge_device_id: string; camera_id: string };
      config: { composite_rule: CompositeRuleV1 };
      alert: { severity: CompositeRuleV1['severity']; enabled: false };
      pattern: { enabled: boolean; min_events?: number; window_seconds?: number; cooldown_seconds?: number } | { enabled: false } | null;
      enabled: true;
    } {
  const rule = canonicalCompositeRulePayload({
    ...readCompositeRule(formData.config),
    name: String(formData.name || '').trim(),
  });
  if (!rule.name) return { error: 'Name is required' };
  if (!rule.rule_id) rule.rule_id = `cmp_${Math.random().toString(16).slice(2, 14)}`;
  const bound = deriveCompositeScopeBinding(rule, formData.sourcePolicies);
  const withBind = { ...rule, scope_binding: bound };
  const derived = deriveCompositeTarget(withBind, formData.sourcePolicies);
  if (!derived.ok) return { error: derived.error };
  const scope = compositeScopeForSave(withBind, formData.sourcePolicies, formData.scope);
  const cameraIds = Array.isArray((scope as { camera_ids?: string[] }).camera_ids)
    ? (scope as { camera_ids: string[] }).camera_ids
    : [];
  const existingTarget = readCompositeTarget(formData);
  const target = derived.target || (cameraIds.length ? existingTarget : null);
  if (cameraIds.length && !target) {
    return { error: COMPOSITE_MISSING_SOURCE_TARGET_COPY };
  }
  if (withBind.scope_binding === 'SAME_CAMERA' && boundIdsPresent(withBind) && !target?.camera_id) {
    return { error: COMPOSITE_MISSING_SOURCE_TARGET_COPY };
  }
  if (target && (!target.edge_device_id || !target.camera_id)) {
    return { error: COMPOSITE_MISSING_SOURCE_TARGET_COPY };
  }
  return {
    policy_type: 'composite_alert',
    name: rule.name,
    scope,
    ...(target ? { target } : {}),
    config: { composite_rule: withBind },
    alert: { severity: rule.severity, enabled: false },
    pattern: formData.pattern?.enabled
      ? {
        enabled: true,
        min_events: Number(formData.pattern.min_events ?? 5),
        window_seconds: Number(formData.pattern.window_seconds ?? 3600),
        cooldown_seconds: Number(formData.pattern.cooldown_seconds ?? 14400),
      }
      : (formData.pattern ? { enabled: false } : null),
    enabled: true,
  };
}

function boundIdsPresent(rule: CompositeRuleV1): boolean {
  return (rule.terms || []).some((term) => Boolean(String(term.source_policy_id || '').trim()));
}

export const CompositeCreateEntry: React.FC<{ onStart: () => void }> = ({ onStart }) => (
  <Paper
    onClick={onStart}
    data-testid="composite-alert-create-entry"
    sx={{
      p: 2,
      mb: 2,
      cursor: 'pointer',
      border: '2px solid',
      borderColor: 'divider',
      '&:hover': { borderColor: 'primary.main' },
    }}
  >
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
      Composite Alert
    </Typography>
    <Typography variant="body2" color="text.secondary">
      Alert when multiple conditions (restricted-area entry, PPE, forklift proximity) occur together. Not a Pattern Alert. Draft only in this phase.
    </Typography>
  </Paper>
);

export function compositeRuntimeBlocked(policyType?: string): string | null {
  return policyType === 'composite_alert' ? COMPOSITE_RUNTIME_NOT_AVAILABLE_COPY : null;
}

export function blockCompositeRuntime(
  policyType: string | undefined,
  setSnackbar: (state: any) => void,
): boolean {
  const blocked = compositeRuntimeBlocked(policyType);
  if (!blocked) return false;
  setSnackbar({ open: true, message: blocked, severity: 'error' });
  return true;
}

export function openCompositePolicy(
  policy: any,
  setters: {
    setEditingPolicy: (policy: any) => void;
    setSelectedPolicyPack: (pack: any) => void;
    setSelectedPolicyAlias: (alias: string) => void;
    setEditorStep: (step: number) => void;
    setFormData: (data: any) => void;
    setPostCreateAction: (action: any) => void;
    setDialogOpen: (open: boolean) => void;
  },
): boolean {
  if (!policy || policy.policy_type !== 'composite_alert') return false;
  setters.setEditingPolicy(policy);
  setters.setSelectedPolicyPack(null);
  setters.setSelectedPolicyAlias('');
  setters.setEditorStep(3);
  setters.setFormData(compositeFormFromStored(policy as any));
  setters.setPostCreateAction('draft');
  setters.setDialogOpen(true);
  return true;
}

export async function saveCompositePolicy(args: {
  formData: {
    policy_type?: string;
    name?: string;
    config?: Record<string, any>;
    scope?: Record<string, any>;
    target?: { edge_device_id?: string | null; camera_id?: string | null } | null;
    pattern?: { enabled?: boolean; min_events?: number; window_seconds?: number; cooldown_seconds?: number } | { enabled: false } | null;
  };
  editingPolicy?: { policy_id: string } | null;
  siteId: string;
  orgId?: string;
  sourcePolicies?: Policy[];
  createPolicy: (siteId: string, payload: any) => Promise<unknown>;
  updatePolicy: (siteId: string, policyId: string, payload: any, orgId?: string) => Promise<unknown>;
  setSnackbar: (state: any) => void;
  onSaved: () => void;
}): Promise<boolean> {
  if (args.formData.policy_type !== 'composite_alert') return false;
  const payload = compositeSavePayload({
    ...args.formData,
    sourcePolicies: args.sourcePolicies,
  });
  if ('error' in payload) {
    args.setSnackbar({ open: true, message: payload.error, severity: 'error' });
    return true;
  }
  if (args.editingPolicy) {
    await args.updatePolicy(args.siteId, args.editingPolicy.policy_id, payload, args.orgId);
  } else {
    await args.createPolicy(args.siteId, payload);
  }
  args.setSnackbar({ open: true, message: 'Composite Alert saved as draft', severity: 'success' });
  args.onSaved();
  return true;
}

export const CompositeEditorSwitch: React.FC<{
  formData: {
    policy_type?: string;
    name?: string;
    config?: Record<string, any>;
    alert?: { severity?: string; enabled?: boolean };
    scope?: Record<string, any>;
    pattern?: { enabled?: boolean; min_events?: number; window_seconds?: number; cooldown_seconds?: number } | { enabled: false } | null;
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  editorStep: number;
  setEditorStep: React.Dispatch<React.SetStateAction<number>>;
  setPostCreateAction: React.Dispatch<React.SetStateAction<'draft' | 'activate' | 'activate_deploy'>>;
  sourcePolicies?: Policy[];
  cameras?: Camera[];
  children: React.ReactNode;
}> = ({ formData, setFormData, editorStep, setEditorStep, setPostCreateAction, sourcePolicies = [], cameras = [], children }) => {
  if (formData.policy_type === 'composite_alert') {
    return (
      <CompositeAlertEditor
        rule={readCompositeRule(formData.config)}
        sourcePolicies={sourcePolicies}
        cameras={cameras}
        pattern={formData.pattern}
        onPatternChange={(next) =>
          setFormData((prev: any) => ({
            ...prev,
            pattern: next,
          }))
        }
        onChange={(rule) =>
          setFormData((prev: any) => {
            const derived = deriveCompositeTarget(rule, sourcePolicies);
            const next: Record<string, any> = {
              ...prev,
              policy_type: 'composite_alert',
              name: rule.name,
              config: { ...(prev.config || {}), composite_rule: rule },
              scope: compositeScopeForSave(rule, sourcePolicies, prev.scope),
              alert: { ...(prev.alert || {}), severity: rule.severity, enabled: false },
            };
            if (derived.ok && derived.target) next.target = derived.target;
            else delete next.target;
            return next;
          })
        }
      />
    );
  }
  return (
    <>
      {editorStep === 0 && (
        <CompositeCreateEntry
          onStart={() => {
            setFormData(emptyCompositeForm());
            setPostCreateAction('draft');
            setEditorStep(3);
          }}
        />
      )}
      {children}
    </>
  );
};
